import React from 'react';
import Icon from '../../../components/AppIcon';

const SystemStatus = ({ 
  systemHealth = 'healthy',
  activeSessions = 0,
  serverLoad = 0,
  lastUpdate = new Date(),
  alerts = []
}) => {
  const getHealthStatus = () => {
    switch (systemHealth) {
      case 'healthy':
        return {
          color: 'text-success',
          bgColor: 'bg-success/10',
          icon: 'CheckCircle',
          label: 'Sistema Operativo',
          description: 'Todos los servicios funcionan correctamente'
        };
      case 'warning':
        return {
          color: 'text-warning',
          bgColor: 'bg-warning/10',
          icon: 'AlertTriangle',
          label: 'Advertencias Menores',
          description: 'Algunos servicios requieren atención'
        };
      case 'critical':
        return {
          color: 'text-error',
          bgColor: 'bg-error/10',
          icon: 'XCircle',
          label: 'Estado Crítico',
          description: 'Servicios principales afectados'
        };
      default:
        return {
          color: 'text-muted-foreground',
          bgColor: 'bg-muted',
          icon: 'HelpCircle',
          label: 'Estado Desconocido',
          description: 'No se puede determinar el estado'
        };
    }
  };

  const getLoadColor = (load) => {
    if (load < 50) return 'text-success';
    if (load < 80) return 'text-warning';
    return 'text-error';
  };

  const getLoadBgColor = (load) => {
    if (load < 50) return 'bg-success';
    if (load < 80) return 'bg-warning';
    return 'bg-error';
  };

  const status = getHealthStatus();
  const formatTime = (date) => {
    return date?.toLocaleTimeString('es-MX', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground">Estado del Sistema</h3>
          <div className="flex items-center space-x-2 text-xs text-muted-foreground">
            <Icon name="Clock" size={14} />
            <span>Actualizado: {formatTime(lastUpdate)}</span>
          </div>
        </div>
      </div>
      <div className="p-6 space-y-6">
        {/* System Health */}
        <div className={`flex items-center space-x-3 p-4 rounded-lg ${status?.bgColor}`}>
          <Icon name={status?.icon} size={24} className={status?.color} />
          <div className="flex-1">
            <h4 className={`font-medium ${status?.color}`}>{status?.label}</h4>
            <p className="text-sm text-muted-foreground">{status?.description}</p>
          </div>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Active Sessions */}
          <div className="bg-muted/30 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="Users" size={16} className="text-primary" />
              <span className="text-sm font-medium text-foreground">Sesiones Activas</span>
            </div>
            <div className="flex items-baseline space-x-2">
              <span className="text-2xl font-semibold text-foreground">{activeSessions}</span>
              <span className="text-sm text-muted-foreground">usuarios</span>
            </div>
          </div>

          {/* Server Load */}
          <div className="bg-muted/30 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="Activity" size={16} className="text-primary" />
              <span className="text-sm font-medium text-foreground">Carga del Servidor</span>
            </div>
            <div className="space-y-2">
              <div className="flex items-baseline space-x-2">
                <span className={`text-2xl font-semibold ${getLoadColor(serverLoad)}`}>
                  {serverLoad}%
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div 
                  className={`h-2 rounded-full transition-all duration-300 ${getLoadBgColor(serverLoad)}`}
                  style={{ width: `${Math.min(serverLoad, 100)}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Security Status */}
        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <Icon name="Shield" size={16} className="text-success" />
              <span className="text-sm font-medium text-foreground">Seguridad</span>
            </div>
            <div className="flex items-center space-x-1 text-success">
              <Icon name="Lock" size={14} />
              <span className="text-xs font-medium">TLS Activo</span>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Firewall:</span>
              <span className="text-success font-medium">Activo</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">2FA:</span>
              <span className="text-success font-medium">Habilitado</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Auditoría:</span>
              <span className="text-success font-medium">Registrando</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Respaldos:</span>
              <span className="text-success font-medium">Actualizados</span>
            </div>
          </div>
        </div>

        {/* Alerts */}
        {alerts?.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-foreground flex items-center space-x-2">
              <Icon name="Bell" size={16} className="text-warning" />
              <span>Alertas del Sistema</span>
            </h4>
            {alerts?.slice(0, 3)?.map((alert, index) => (
              <div key={index} className={`flex items-start space-x-3 p-3 rounded-lg ${
                alert?.severity === 'high' ? 'bg-error/10' :
                alert?.severity === 'medium' ? 'bg-warning/10' : 'bg-muted/50'
              }`}>
                <Icon 
                  name={alert?.severity === 'high' ? 'AlertTriangle' : 'Info'} 
                  size={16} 
                  className={
                    alert?.severity === 'high' ? 'text-error' :
                    alert?.severity === 'medium' ? 'text-warning' : 'text-muted-foreground'
                  }
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground">{alert?.message}</p>
                  <p className="text-xs text-muted-foreground">{formatTime(alert?.timestamp)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SystemStatus;